import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Telegram as TelegramIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SectorEnum } from '../../utils/SectorEnum';

export interface EmailContact {
  id: string;
  email: string;
  name: string;
  role: string; // Papel / Área de Atuação
  sector: string; // Setor (enum-backed)
}

export interface TelegramContact {
  id: string;
  number: string;
  name: string;
  role: string; // Papel / Área de Atuação
  sector: string; // Setor (enum-backed)
  isGroup: boolean;
}

export interface WhatsAppContact {
  id: string;
  number: string;
  name: string;
  role: string; // Papel / Área de Atuação
  sector: string; // Setor (enum-backed)
}

export interface ContactData {
  emails: EmailContact[];
  telegrams: TelegramContact[];
  whatsapps: WhatsAppContact[];
}

interface ContactManagerProps {
  title?: string;
  contacts: ContactData;
  onChange: (contacts: ContactData) => void;
  allowAddTelegram?: boolean;
  allowAddWhatsApp?: boolean;
  telegramEmptyContent?: React.ReactNode;
  whatsAppEmptyContent?: React.ReactNode;
  notifyTelegram?: boolean;
  onNotifyTelegramChange?: (value: boolean) => void;
  telegramGroupUrl?: string;
  telegramStatusAlertMessage?: string;
  allowedSectorValues?: number[];
  hideTelegramSection?: boolean;
}

  const ContactManager: React.FC<ContactManagerProps> = ({
    title,
    contacts,
    onChange,
    allowAddTelegram = true,
    allowAddWhatsApp = true,
    telegramEmptyContent,
    whatsAppEmptyContent,
    notifyTelegram,
    onNotifyTelegramChange,
    telegramGroupUrl,
    telegramStatusAlertMessage,
    allowedSectorValues,
    hideTelegramSection = false
  }) => {
  const { t } = useTranslation();
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Opções do dropdown de setor (refatorado para usar apenas os setores definidos)
  const sectorOptions: { value: number; label: string }[] = [
    { value: SectorEnum.NONE, label: t('contact_manager_role_options_select') },
    { value: SectorEnum.ADMINISTRADOR, label: t('contact_manager_role_admin') },
    { value: SectorEnum.ENGENHARIA_DE_SOFTWARE, label: t('contact_manager_role_engineering') },
    { value: SectorEnum.FINANCEIRO, label: t('contact_manager_role_finance') },
    { value: SectorEnum.GESTAO, label: t('contact_manager_role_management') },
    { value: SectorEnum.MARKETING, label: t('contact_manager_role_marketing') },
    { value: SectorEnum.CEO, label: t('contact_manager_role_ceo') },
    // "Outro" deve ser o último
    { value: SectorEnum.OUTRO, label: t('contact_manager_role_other') }
  ];

  const sectorOptionsFiltered: { value: number; label: string }[] = Array.isArray(allowedSectorValues)
    ? sectorOptions.filter(opt => opt.value === 0 || allowedSectorValues.includes(opt.value))
    : sectorOptions;

  const addEmail = () => {
    const newEmail: EmailContact = {
      id: generateId(),
      email: '',
      name: '',
      role: '',
      sector: '0'
    };
    onChange({
      ...contacts,
      emails: [...contacts.emails, newEmail]
    });
  };

  const updateEmail = (id: string, field: keyof EmailContact, value: string) => {
    onChange({
      ...contacts,
      emails: contacts.emails.map(email =>
        email.id === id ? { ...email, [field]: value } : email
      )
    });
  };

  const removeEmail = (id: string) => {
    onChange({
      ...contacts,
      emails: contacts.emails.filter(email => email.id !== id)
    });
  };

  const addTelegram = () => {
    const newTelegram: TelegramContact = {
      id: generateId(),
      number: '',
      name: '',
      role: '',
      sector: '0',
      isGroup: false
    };
    onChange({
      ...contacts,
      telegrams: [...contacts.telegrams, newTelegram]
    });
  };

  const updateTelegram = (id: string, field: keyof TelegramContact, value: string | boolean) => {
    onChange({
      ...contacts,
      telegrams: contacts.telegrams.map(telegram =>
        telegram.id === id ? { ...telegram, [field]: value } : telegram
      )
    });
  };

  const removeTelegram = (id: string) => {
    onChange({
      ...contacts,
      telegrams: contacts.telegrams.filter(telegram => telegram.id !== id)
    });
  };

  const addWhatsApp = () => {
    const newWhatsApp: WhatsAppContact = {
      id: generateId(),
      number: '',
      name: '',
      role: '',
      sector: '0'
    };
    onChange({
      ...contacts,
      whatsapps: [...contacts.whatsapps, newWhatsApp]
    });
  };

  const updateWhatsApp = (id: string, field: keyof WhatsAppContact, value: string) => {
    onChange({
      ...contacts,
      whatsapps: contacts.whatsapps.map(whatsapp =>
        whatsapp.id === id ? { ...whatsapp, [field]: value } : whatsapp
      )
    });
  };

  const removeWhatsApp = (id: string) => {
    onChange({
      ...contacts,
      whatsapps: contacts.whatsapps.filter(whatsapp => whatsapp.id !== id)
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {title ?? t('contact_manager_title_default')}
      </Typography>

      {/* Email Contacts */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">{t('contact_manager_emails_title')}</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addEmail}
              sx={{ ml: 'auto' }}
              size="small"
            >
              {t('contact_manager_add_btn')}
            </Button>
          </Box>

          {contacts.emails.map((email) => (
            <Box key={email.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid component={'div' as React.ElementType} item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={t('contact_manager_field_email_label')}
                    type="email"
                    value={email.email}
                    onChange={(e) => updateEmail(email.id, 'email', e.target.value)}
                    size="small"
                    required
                    inputProps={{ maxLength: 80 }}
                  />
                </Grid>
                <Grid component={'div' as React.ElementType} item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label={t('contact_manager_field_name_label')}
                    value={email.name}
                    onChange={(e) => updateEmail(email.id, 'name', e.target.value)}
                    size="small"
                    required
                    inputProps={{ maxLength: 80 }}
                  />
                </Grid>
                    <Grid component={'div' as React.ElementType} item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('contact_manager_field_role_label')}</InputLabel>
                    <Select
                      value={(email.sector && String(email.sector).trim() !== '') ? String(email.sector) : '0'}
                      label={t('contact_manager_field_role_label')}
                      onChange={(e) => updateEmail(email.id, 'sector', String(e.target.value))}
                      displayEmpty
                      renderValue={(selected) => {
                        const val = String(selected ?? '0');
                        const opt = sectorOptions.find((o) => String(o.value) === val);
                        const isPlaceholder = val === '0';
                        const label = opt ? opt.label : '';
                        return (
                          <span style={{ fontStyle: isPlaceholder ? 'italic' : 'normal', color: isPlaceholder ? 'rgba(0,0,0,0.6)' : undefined }}>
                            {label}
                          </span>
                        );
                      }}
                    >
                      {sectorOptionsFiltered.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value.toString()}
                          sx={option.value === 0 ? { fontStyle: 'italic', color: 'text.secondary' } : undefined}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid component={'div' as React.ElementType} item xs={12} sm={12}>
                  <TextField
                    fullWidth
                    label={t('contact_manager_field_papel_label')}
                    value={email.role}
                    onChange={(e) => updateEmail(email.id, 'role', e.target.value)}
                    size="small"
                    inputProps={{ maxLength: 120 }}
                  />
                </Grid>
            
                <Grid component={'div' as React.ElementType} item xs={12} sm={2}>
                  <IconButton
                    onClick={() => removeEmail(email.id)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          ))}

          {contacts.emails.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              {t('contact_manager_emails_empty')}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Telegram Contacts */}
      {!hideTelegramSection && (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TelegramIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">{t('contact_manager_telegrams_title')}</Typography>
            {allowAddTelegram && (
              <Button
                startIcon={<AddIcon />}
                onClick={addTelegram}
                sx={{ ml: 'auto' }}
                size="small"
              >
                {t('contact_manager_add_btn')}
              </Button>
            )}
          </Box>

          {onNotifyTelegramChange && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('contact_manager_notify_group_title')}
              </Typography>
              <RadioGroup
                row
                value={notifyTelegram ? 'true' : 'false'}
                onChange={(e) => onNotifyTelegramChange(e.target.value === 'true')}
              >
                <FormControlLabel value="true" control={<Radio />} label={t('contact_manager_enabled')} />
                <FormControlLabel value="false" control={<Radio />} label={t('contact_manager_disabled')} />
              </RadioGroup>
              {notifyTelegram && telegramGroupUrl && (
                <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                  {t('contact_manager_telegram_group_label')} <a href={telegramGroupUrl} target="_blank" rel="noopener noreferrer">{telegramGroupUrl}</a>
                </Alert>
              )}
            </Box>
          )}

          {contacts.telegrams.map((telegram) => (
            <Box key={telegram.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid component={'div' as React.ElementType} item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label={t('contact_manager_field_number_label')}
                    value={telegram.number}
                    onChange={(e) => {
                      const sanitized = e.target.value.replace(/[^0-9+\-\s]/g, '').slice(0, 15);
                      updateTelegram(telegram.id, 'number', sanitized);
                    }}
                    size="small"
                    placeholder="+55 11 99999-9999"
                    inputProps={{ maxLength: 15 }}
                  />
                </Grid>
                <Grid component={'div' as React.ElementType} item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label={t('contact_manager_field_name_label')}
                    value={telegram.name}
                    onChange={(e) => updateTelegram(telegram.id, 'name', e.target.value)}
                    size="small"
                    required
                    inputProps={{ maxLength: 80 }}
                  />
                </Grid>
                <Grid component={'div' as React.ElementType} item xs={12} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('contact_manager_field_role_label')}</InputLabel>
                    <Select
                      value={(telegram.sector && String(telegram.sector).trim() !== '') ? String(telegram.sector) : '0'}
                      label={t('contact_manager_field_role_label')}
                      onChange={(e) => updateTelegram(telegram.id, 'sector', String(e.target.value))}
                      displayEmpty
                      renderValue={(selected) => {
                        const val = String(selected ?? '0');
                        const opt = sectorOptions.find((o) => String(o.value) === val);
                        const isPlaceholder = val === '0';
                        const label = opt ? opt.label : '';
                        return (
                          <span style={{ fontStyle: isPlaceholder ? 'italic' : 'normal', color: isPlaceholder ? 'rgba(0,0,0,0.6)' : undefined }}>
                            {label}
                          </span>
                        );
                      }}
                    >
                      {sectorOptionsFiltered.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value.toString()}
                          sx={option.value === 0 ? { fontStyle: 'italic', color: 'text.secondary' } : undefined}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid component={'div' as React.ElementType} item xs={12} sm={12}>
                  <TextField
                    fullWidth
                    label={t('contact_manager_field_papel_label')}
                    value={telegram.role}
                    onChange={(e) => updateTelegram(telegram.id, 'role', e.target.value)}
                    size="small"
                    inputProps={{ maxLength: 120 }}
                  />
                </Grid>
                <Grid component={'div' as React.ElementType} item xs={12} sm={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={telegram.isGroup}
                        onChange={(e) => updateTelegram(telegram.id, 'isGroup', e.target.checked)}
                        size="small"
                      />
                    }
                    label={t('contact_manager_field_group_label')}
                  />
                </Grid>
                <Grid component={'div' as React.ElementType} item xs={12} sm={2}>
                  <IconButton
                    onClick={() => removeTelegram(telegram.id)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          ))}

          {contacts.telegrams.length === 0 && telegramEmptyContent && (
            <Box sx={{ p: 2 }}>
              {onNotifyTelegramChange ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('contact_manager_notify_group_title')}
                  </Typography>
                  <RadioGroup
                    row
                    value={notifyTelegram ? 'true' : 'false'}
                    onChange={(e) => onNotifyTelegramChange(e.target.value === 'true')}
                  >
                    <FormControlLabel value="true" control={<Radio />} label={t('contact_manager_enabled')} />
                    <FormControlLabel value="false" control={<Radio />} label={t('contact_manager_disabled')} />
                  </RadioGroup>
                  {telegramGroupUrl && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {t('contact_manager_telegram_group_label')} <a href={telegramGroupUrl} target="_blank" rel="noreferrer">{telegramGroupUrl}</a>
                    </Typography>
                  )}
                </Box>
              ) : telegramEmptyContent ? (
                telegramEmptyContent
              ) : (
                <Alert severity="info" variant="outlined">
                  {t('contact_manager_telegrams_empty_default')}
                </Alert>
              )}
            </Box>
          )}

          {telegramStatusAlertMessage && (
            <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
              {telegramStatusAlertMessage}
            </Alert>
          )}
        </CardContent>
      </Card>
      )}

      {/* WhatsApp Contacts — commented out
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <WhatsAppIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">{t('contact_manager_whatsapps_title')}</Typography>
            {allowAddWhatsApp && (
              <Button
                startIcon={<AddIcon />}
                onClick={addWhatsApp}
                sx={{ ml: 'auto' }}
                size="small"
              >
                {t('contact_manager_add_btn')}
              </Button>
            )}
          </Box>

          {contacts.whatsapps.map((whatsapp) => (
            <Box key={whatsapp.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid component={'div' as React.ElementType} item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={t('contact_manager_field_number_label')}
                    value={whatsapp.number}
                    onChange={(e) => {
                      const sanitized = e.target.value.replace(/[^0-9+\-\s]/g, '').slice(0, 15);
                      updateWhatsApp(whatsapp.id, 'number', sanitized);
                    }}
                    size="small"
                    placeholder="+55 11 99999-9999"
                    inputProps={{ maxLength: 15 }}
                  />
                </Grid>
                <Grid component={'div' as React.ElementType} item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label={t('contact_manager_field_name_label')}
                    value={whatsapp.name}
                    onChange={(e) => updateWhatsApp(whatsapp.id, 'name', e.target.value)}
                    size="small"
                    required
                    inputProps={{ maxLength: 80 }}
                  />
                </Grid>
                <Grid component={'div' as React.ElementType} item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('contact_manager_field_role_label')}</InputLabel>
                    <Select
                      value={(whatsapp.sector && String(whatsapp.sector).trim() !== '') ? String(whatsapp.sector) : '0'}
                      label={t('contact_manager_field_role_label')}
                      onChange={(e) => updateWhatsApp(whatsapp.id, 'sector', String(e.target.value))}
                      displayEmpty
                      renderValue={(selected) => {
                        const val = String(selected ?? '0');
                        const opt = sectorOptions.find((o) => String(o.value) === val);
                        const isPlaceholder = val === '0';
                        const label = opt ? opt.label : '';
                        return (
                          <span style={{ fontStyle: isPlaceholder ? 'italic' : 'normal', color: isPlaceholder ? 'rgba(0,0,0,0.6)' : undefined }}>
                            {label}
                          </span>
                        );
                      }}
                    >
                      {sectorOptionsFiltered.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value.toString()}
                          sx={option.value === 0 ? { fontStyle: 'italic', color: 'text.secondary' } : undefined}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid component={'div' as React.ElementType} item xs={12} sm={12}>
                  <TextField
                    fullWidth
                    label={t('contact_manager_field_papel_label')}
                    value={whatsapp.role}
                    onChange={(e) => updateWhatsApp(whatsapp.id, 'role', e.target.value)}
                    size="small"
                    inputProps={{ maxLength: 120 }}
                  />
                </Grid>
                <Grid component={'div' as React.ElementType} item xs={12} sm={2}>
                  <IconButton
                    onClick={() => removeWhatsApp(whatsapp.id)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          ))}

          {contacts.whatsapps.length === 0 && (
            <Box sx={{ p: 2 }}>
              {whatsAppEmptyContent ? (
                whatsAppEmptyContent
              ) : (
                <Alert severity="info" variant="outlined">
                  {t('contact_manager_whatsapps_empty_default')}
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
      */}
    </Box>
  );
};

export default ContactManager;